type PromoEntryBootstrap = {
  capture: () => void
  isSensitivePath: (path: string) => boolean
  credential: () => string | null
  consume: (expected: string) => boolean
  isSensitive: () => boolean
  markSensitive: () => void
}

declare global {
  interface Window {
    __doraPromoEntry?: PromoEntryBootstrap
  }
}

// The inline bootstrap in index.html owns capture and URL cleanup, before any module loads.
export const protectPromoEntry = () => window.__doraPromoEntry?.capture()
export const getPromoEntryCredential = () => window.__doraPromoEntry?.credential() ?? null
export const consumePromoEntryCredential = (expected: string) =>
  window.__doraPromoEntry?.consume(expected) ?? false
export const isSensitivePromoDocument = () => window.__doraPromoEntry?.isSensitive() ?? true
export const markSensitivePromoDocument = () => window.__doraPromoEntry?.markSensitive()
export const isPromoEntryPath = (path: string) =>
  window.__doraPromoEntry?.isSensitivePath(path) ?? true

type PromoLoginValidation = { userId: string } | { error: 'invalid' | 'unavailable' }

export async function validatePromoLogin({
  apiKey,
  baseUrl,
  tenantId,
  signal,
  fetcher = fetch,
  timeoutMs = 15_000,
}: {
  apiKey: string
  baseUrl: string
  tenantId: string
  signal: AbortSignal
  fetcher?: typeof fetch
  timeoutMs?: number
}): Promise<PromoLoginValidation> {
  if (signal.aborted) return { error: 'unavailable' }
  const controller = new AbortController()
  const abort = () => controller.abort()
  signal.addEventListener('abort', abort, { once: true })
  const timeout = setTimeout(abort, timeoutMs)
  try {
    const response = await fetcher(new URL('/v1/user/self', baseUrl).href, {
      method: 'GET',
      headers: {
        Authorization: `ApiKey ${apiKey}`,
        'tenant-id': tenantId,
        Accept: 'application/json',
      },
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'omit',
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
    })
    // The self route redirects to the user URL. Fetch strips Authorization on
    // cross-origin redirects; never accept an identity returned by another origin.
    if (response.url && new URL(response.url).origin !== new URL(baseUrl).origin) {
      return { error: 'invalid' }
    }
    if (!response.ok) {
      return { error: [401, 403, 404].includes(response.status) ? 'invalid' : 'unavailable' }
    }
    const body: unknown = await response.json()
    const data = (body as { data?: { id?: unknown; tenant_id?: unknown } } | null)?.data
    if (typeof data?.id !== 'string' || !data.id.trim() || data.tenant_id !== tenantId) {
      return { error: 'invalid' }
    }
    return { userId: data.id }
  } catch {
    // Never retain errors or response bodies: transport errors may include the credential.
    return { error: 'unavailable' }
  } finally {
    clearTimeout(timeout)
    signal.removeEventListener('abort', abort)
  }
}
